import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { updateProjectsStagesStatus, IProjectStage, ISortProject, handleUpdateCurrentActiveStepApi, Stagestep, CurrentStageStep, findPrevStep, findStepIndex, findNextStep } from '../../../modules/project'
import { Field, Form, Formik, FormikProps, setIn } from 'formik'
import { KTIcon } from '../../../../_theme/helpers'
import NotFound from '../../apps/components/NotFound'
import { useSnackbar } from '../../../../_theme/context/SnackbarContext'
import { ApiResponse, IDocumentData } from '../../core/_models'
import { useNavigate } from 'react-router-dom'
import { getProjectStageLink, stageCompletedCode } from '../../../../utils/config'
import SubmitConfirmationModal from '../../apps/components/SubmitConfirmationModal'
import StepChangeAlertModal from '../../apps/components/StepChangeAlertModal'
import {
    Layers,
    Send,
    CircleDot,
    Globe,
    TrendingUp,
    ChartColumn,
    DollarSign,
    FileText,
    Shield,
    Zap,
    Scale,
    Users
} from "lucide-react";
import DynamicSymbol from '../../apps/components/DynamicSymbol'
import ThemeTooltip from '../../apps/components/UI/ThemeTooltip'
import { strategicResourcingStageSchema, IStrategicResourcingStageSchema } from '../components/SR-steps/core/_validation'
import StepIconSidebar from './StepIconSidebar'
import SimpleLoader from '../../apps/components/SimpleLoader'
import { strategicResourcingDefaultValues } from '../components/SR-steps/core/defaultValues'
import { IBaseCaseAndCounterfactualScenarioEngine, ICbaCeaIntelligenceEngine, IDemandAndNeedsValidationEngine, IEconomiesOfScaleAssessment, IProjectFeasibilityAndOptionsAnalysis, IFinancialAndFundingStrategyEngine, IFiscalSustainabilityEngine, IRiskAndSensitivityAnalysisEngine, ISpecialProcurementActionsAndSocialImpact, IStrategicResourcingStepTypes } from '../components/SR-steps/core/_models'
import { saveProjectStageTwo, StoreorUpdateJustificationForOptions, storeOrUpdateSensitivityAnalysisEngine, updateBaseCaseAndCounterfactualScenario, updateCbaCeaIntelligenceEngine, updateDemandAndNeedsValidationEngine, updateEconomiesOfScaleAssessment, updateFinancialAndFundingStrategy, updateFiscalSustainability, updateSpecialProcurementActionsAndSocialImpact } from '../components/SR-steps/core/_request'
import { LogError } from '../../../../utils/GlobalLogging'
import { updateProjectDocuments } from '../core/document/_request'


const SubmitStep = lazy(() => import('../components/common/SubmitStep').then(module => ({ default: module.SubmitStep })));
import { getApiErrorMessage } from "../../../../utils/utils";
const FeasibilityOptionsAnalysis = lazy(() => import('../components/SR-steps/FeasibilityOptionsAnalysis').then(module => ({ default: module.FeasibilityOptionsAnalysis })));
const EconomiesOfScaleAssessment = lazy(() => import('../components/SR-steps/EconomiesOfScaleAssessment').then(module => ({ default: module.EconomiesOfScaleAssessment })));
const SpecialProcurementActionsAndSocialImpact = lazy(() => import('../components/SR-steps/SpecialProcurementActionsAndSocialImpact').then(module => ({ default: module.SpecialProcurementActionsAndSocialImpact })));
const BaseCaseAndCounterfactualScenarioEngine = lazy(() => import('../components/SR-steps/BaseCaseAndCounterfactualScenarioEngine').then(module => ({ default: module.BaseCaseAndCounterfactualScenarioEngine })));
const DemandAndNeedsValidationEngine = lazy(() => import('../components/SR-steps/DemandAndNeedsValidationEngine').then(module => ({ default: module.DemandAndNeedsValidationEngine })));
const CBACEAiIntelligenceEngine = lazy(() => import('../components/SR-steps/CBACEAiIntelligenceEngine').then(module => ({ default: module.CBACEAiIntelligenceEngine })));
const FinancialAndFundingStrategyEngine = lazy(() => import('../components/SR-steps/FinancialAndFundingStrategyEngine').then(module => ({ default: module.FinancialAndFundingStrategyEngine })));
const FiscalSustainabilityEngineIFSA = lazy(() => import('../components/SR-steps/FiscalSustainabilityEngineIFSA').then(module => ({ default: module.FiscalSustainabilityEngineIFSA })));
const RiskAndSensitivityAnalysisEngine = lazy(() => import('../components/SR-steps/RiskAndSensitivityAnalysisEngine').then(module => ({ default: module.RiskAndSensitivityAnalysisEngine })));
const StrategicResourcingDecisionEngineCoreOutput = lazy(() => import('../components/SR-steps/StrategicResourcingDecisionEngineCoreOutput').then(module => ({ default: module.StrategicResourcingDecisionEngineCoreOutput })));
const Documents = lazy(() => import('../components/common/DocumentStep').then(module => ({ default: module.DocumentStep })));
type Props = {
    project: ISortProject;
    stage: IProjectStage;
    isApprover: boolean;
    readonlyAccess: boolean;
    canSubmit: boolean;
    user_id: string;
}

const READ_ONLY_STATUSES = ["stage_status_3", "stage_status_4", "stage_status_6"];

const baseStepperData: Stagestep[] = [
    {
        title: "Base Case & Counterfactual Scenario Engine",
        key: "base_case_and_counterfactual_scenario_engine",
        icon: <Globe size={22} />,
        iconColor: "text-success",
        iconBackground: "bg-light-success",
        description: "",
        components: BaseCaseAndCounterfactualScenarioEngine,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Options Identification & Screening Engine",
        key: "options_library_engine",
        icon: <Layers size={22} />,
        iconColor: "text-warning",
        iconBackground: "bg-light-warning",
        description: "",
        components: FeasibilityOptionsAnalysis,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Demand & Needs Validation Engine",
        key: "demand_and_needs_validation_engine",
        icon: <TrendingUp size={22} />,
        iconColor: "text-primary",
        iconBackground: "bg-light-primary",
        description: "",
        components: DemandAndNeedsValidationEngine,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "CBA / CEA Intelligence Engine",
        key: "cba_cea_intelligence_engine",
        icon: <ChartColumn size={22} />,
        iconColor: "text-info",
        iconBackground: "bg-light-info",
        description: "",
        components: CBACEAiIntelligenceEngine,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Financial & Funding Strategy Engine",
        key: "financial_and_funding_strategy_engine",
        icon: <DollarSign size={22} />,
        iconColor: "text-warning",
        iconBackground: "bg-light-warning",
        description: "",
        components: FinancialAndFundingStrategyEngine,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Fiscal Sustainability Engine — IFISA",
        key: "fiscal_sustainability_engine_ifsa",
        icon: <FileText size={22} />,
        iconColor: "text-danger",
        iconBackground: "bg-light-danger",
        description: "",
        components: FiscalSustainabilityEngineIFSA,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Risk & Sensitivity Analysis Engine",
        key: "risk_and_sensitivity_analysis_engine",
        icon: <Shield size={22} />,
        iconColor: "text-danger",
        iconBackground: "bg-light-danger",
        description: "",
        components: RiskAndSensitivityAnalysisEngine,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Special (e.g. Gender) Procurement Actions & Social Impact",
        key: "special_procurement_actions_and_social_impact",
        icon: <Users size={22} />,
        iconColor: "text-info",
        iconBackground: "bg-light-info",
        description: "",
        components: SpecialProcurementActionsAndSocialImpact,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Economies of Scale",
        key: "economies_of_scale_engine",
        icon: <Scale size={22} />,
        iconColor: "text-success",
        iconBackground: "bg-light-success",
        description: "",
        components: EconomiesOfScaleAssessment,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Documents",
        key: "documents",
        icon: <FileText size={22} />,
        iconColor: "text-warning",
        iconBackground: "bg-light-warning",
        description: "",
        components: Documents,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Strategic Resourcing Decision Engine — Core Output",
        key: "strategic_resourcing_decision_engine_core_output",
        icon: <Zap size={22} />,
        iconColor: "text-info",
        iconBackground: "bg-light-info",
        description: "",
        components: StrategicResourcingDecisionEngineCoreOutput,
        showIndex: false,
        isAllowNextStep: true,
        showContinue: true,
    }
];

const StrategicResourcing: React.FC<Props> = ({ user_id, project, stage, isApprover, readonlyAccess, canSubmit }) => {
    const project_id = project.project_id;

    const stepperData: Stagestep[] = useMemo(() => {
        return [
            ...baseStepperData,
            {
                title: stage.send_to_approval === "yes"
                    ? "Approval"
                    : "Submit",
                key: "approval_submit",
                showIndex: false,
                icon: <Send size={22} />,
                iconColor: "text-primary",
                iconBackground: "bg-primary-light",
                description:
                    stage.send_to_approval === "yes"
                        ? "Submit the project for approval"
                        : "Submit the project",
                components: SubmitStep
            }
        ];
    }, [stage.send_to_approval]);

    const { showSnackbar } = useSnackbar();

    const navigate = useNavigate();
    const [disableFormFields, setDisableFormFields] = useState<boolean>(false);
    const [submitLoader, setSubmitLoader] = useState<boolean>(false);

    const [currentSchema, setCurrentSchema] = useState(strategicResourcingStageSchema[stepperData[0].key as IStrategicResourcingStageSchema]);
    const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false);
    const [stepChangeAlertModal, setStepChnageAlertModal] = useState(false)
    const [pendingStep, setPendingStep] = useState<string | null>(null);

    const [initValues, setInitValues] = useState<IStrategicResourcingStepTypes>(strategicResourcingDefaultValues.base_case_and_counterfactual_scenario_engine);
    const formikRef = useRef<FormikProps<IStrategicResourcingStepTypes>>(null);
    const [validateOnStepChange, setValidateOnStepChange] = useState(false);
    const [currentStep, setCurrentStep] = useState<CurrentStageStep>(() => {
        const stored = stage.active_step ? stage.active_step.toString() : null;
        return findStepIndex(stepperData, stored !== null ? stored : "project_overview", true);
    });

    const [isAllowNextStep, setIsAllowNextStep] = useState(true);

    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        setCurrentSchema(
            strategicResourcingStageSchema[currentStep.step.key as IStrategicResourcingStageSchema]
        );
        setInitValues(
            strategicResourcingDefaultValues[
            currentStep.step.key as IStrategicResourcingStageSchema
            ] as IStrategicResourcingStepTypes
        );
        setIsAllowNextStep(currentStep.step?.isAllowNextStep === false ? false : true);
    }, [currentStep]);

    useEffect(() => {
        setDisableFormFields(isApprover || readonlyAccess || READ_ONLY_STATUSES.includes(stage.dk_stage_status));
    }, [isApprover, readonlyAccess, stage])

    //  Fixed handleUpdateActiveStep with useCallback
    const handleUpdateActiveStep = useCallback(async (activeStep: string) => {
        const response = await handleUpdateCurrentActiveStepApi(stage.dk_stage, activeStep, Number(project_id));
        if (response.status !== "ok") {
            showSnackbar(response.message, 'danger');
        }
    }, [stage.dk_stage, project_id, showSnackbar]);

    //  Fixed handleSaveAndContinue
    const handleSaveAndContinue = useCallback(async (
        values: IStrategicResourcingStepTypes,
    ) => {
        try {
            await submitStep(values);
            if (pendingStep) {
                setCurrentStep(findStepIndex(stepperData, pendingStep));
                setPendingStep(null);
            }
        } catch (err) {
            console.error("Error saving before moving:", err);
        } finally {
            setStepChnageAlertModal(false);
        }
    }, [pendingStep, stepperData]);

    //  Fixed confirmStepChange
    const confirmStepChange = useCallback(() => {
        if (pendingStep) {
            setCurrentStep(findStepIndex(stepperData, pendingStep));
            setPendingStep(null);
        }
        setStepChnageAlertModal(false);
    }, [pendingStep, stepperData]);

    //  Fixed submitStep with useCallback
    const submitStep = useCallback(async (values: IStrategicResourcingStepTypes) => {
        if (isApprover) return;
        setSubmitLoader(true);
        try {
            const projectId = Number(project_id);
            const handlers: Record<
                string,
                () => Promise<ApiResponse>
            > = {
                base_case_and_counterfactual_scenario_engine: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as IBaseCaseAndCounterfactualScenarioEngine),
                        project_id: projectId,
                    }

                    return updateBaseCaseAndCounterfactualScenario(payload);
                },

                options_library_engine: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as IProjectFeasibilityAndOptionsAnalysis),
                        project_id: projectId,
                    }
                    return StoreorUpdateJustificationForOptions(payload);
                },
                demand_and_needs_validation_engine: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as IDemandAndNeedsValidationEngine),
                        project_id: projectId,
                    };

                    return updateDemandAndNeedsValidationEngine(payload);
                },
                cba_cea_intelligence_engine: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as ICbaCeaIntelligenceEngine),
                        project_id: projectId,
                    };

                    return updateCbaCeaIntelligenceEngine(payload);
                },
                financial_and_funding_strategy_engine: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as IFinancialAndFundingStrategyEngine),
                        project_id: projectId,
                    }

                    return updateFinancialAndFundingStrategy(payload);
                },
                fiscal_sustainability_engine_ifsa: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as IFiscalSustainabilityEngine),
                        project_id: projectId,
                    }
                    return updateFiscalSustainability(payload);
                },
                risk_and_sensitivity_analysis_engine: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as IRiskAndSensitivityAnalysisEngine),
                        project_id: projectId,
                    }
                    return storeOrUpdateSensitivityAnalysisEngine(payload);
                },
                economies_of_scale_engine: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as IEconomiesOfScaleAssessment),
                        project_id: projectId,
                    }
                    return updateEconomiesOfScaleAssessment(payload);
                },
                special_procurement_actions_and_social_impact: async () => {
                    const {
                        ...payload
                    } = {
                        ...(values as ISpecialProcurementActionsAndSocialImpact),
                        project_id: projectId,
                    }
                    return updateSpecialProcurementActionsAndSocialImpact(payload);
                },
                documents: async () => updateProjectDocuments({ documents: (values as IDocumentData).documents, project_id: projectId, stage_id: stage.dk_stage }),
            };

            const handler = handlers[currentStep.step.key];

            const response: ApiResponse = handler
                ? await handler()
                : {
                    status: "error",
                    message: "Something went wrong",
                    data: null,
                    errorCode: "",
                };

            if (response.status === "ok") {
                showSnackbar(response.message, "success");

                let activeStep = currentStep.step.key;

                if (values.formAction === "save-next") {
                    const nextStep = findNextStep(currentStep, stepperData);
                    activeStep = nextStep.step.key;
                    setCurrentStep(nextStep);
                } else if (response.data) {
                    formikRef.current?.resetForm({
                        values:
                            response.data as IStrategicResourcingStepTypes,
                    });
                }

                if (stage.active_step !== activeStep) {
                    handleUpdateActiveStep(activeStep);
                }
            } else {
                showSnackbar(response.message, "danger");
                LogError(response.message || "Error submitting step", response, "StrategicResourcing");
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "Something went wrong"), "danger");
            LogError("Error submitting step", error, "StrategicResourcing");
        } finally {
            setSubmitLoader(false);
        }
    }, [isApprover, project_id, currentStep, stepperData, stage.active_step, showSnackbar, handleUpdateActiveStep]);

    //  Fixed handleSubmitStage
    const handleSubmitStage = useCallback(async () => {
        // find first incomplete step
        // TODO: Implement this later
        /*const incompleteIndex = stepInits.findIndex(
            step => !step.iscompleted
        );
        if (incompleteIndex !== -1 && incompleteIndex != stepInits.length - 1) {
            const targetStep = incompleteIndex + 1;
            // move to that step
            setCurrentStep(findStepIndex(stepperData, stepperData[targetStep].key));
            setValidateOnStepChange(true);
            return;
        }*/

        setShowSubmitConfirmationModal(true);
    }, []);

    //  Fixed validation useEffect with cleanup
    useEffect(() => {
        if (!validateOnStepChange) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            if (formikRef.current) {
                formikRef.current.validateForm().then(errors => {
                    const touchedFields = Object.keys(errors).reduce(
                        (acc, key) => {
                            return setIn(acc, key, true);
                        },
                        {}
                    );
                    formikRef.current?.setTouched(touchedFields);
                });
            }
        }, 100);

        setValidateOnStepChange(false);

    }, [initValues, validateOnStepChange]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    //  Fixed handleConfirmSubmitStage
    const handleConfirmSubmitStage = useCallback(async () => {
        setSubmitLoader(true);
        const data = {
            project_id: Number(project_id),
            dk_stage: stage.dk_stage,
        }

        let stageSubmitResp = await saveProjectStageTwo(data);
        if (stageSubmitResp.status === "ok") {
            // if (stage.send_to_approval === "no") {
            //     const data = {
            //         user_id: user_id,
            //         project_id: project_id,
            //         dk_stage: stage.dk_stage
            //     }
            //     await getProjectReport(data, stage.dk_stage);
            // }
            showSnackbar(stageSubmitResp.message, 'success');
            navigate(getProjectStageLink(project_id));
        } else {
            showSnackbar(stageSubmitResp.message, 'danger');
        }
        setSubmitLoader(false);
        setShowSubmitConfirmationModal(false); //  Fixed: Close modal after handling
    }, [user_id, project_id, stage.dk_stage, stage.send_to_approval, navigate, showSnackbar]);

    //  Fixed prevStep
    const prevStep = useCallback((dirty: boolean) => {
        const prev = findPrevStep(currentStep, stepperData);
        if (!["stage_status_3", "stage_status_4", "stage_status_6"].includes(stage.dk_stage_status) && dirty) {
            setPendingStep(prev.step.key);
            setStepChnageAlertModal(true);
            return;
        }
        setCurrentStep(prev)
    }, [currentStep, stepperData, stage.dk_stage_status]);

    const handleContinue = useCallback(() => {
        const nextStep = findNextStep(currentStep, stepperData);
        setCurrentStep(nextStep);
        handleUpdateActiveStep(nextStep.step.key);
    }, [currentStep, stepperData, handleUpdateActiveStep]);

    //  Fixed goToStep
    const goToStep = useCallback((key: string, dirty: boolean) => {
        if (!["stage_status_3", "stage_status_4", "stage_status_6"].includes(stage.dk_stage_status) && dirty) {
            setPendingStep(key);
            setStepChnageAlertModal(true);
            return;
        }
        setCurrentStep(findStepIndex(stepperData, key))
    }, [stepperData, stage.dk_stage_status]);

    //  Fixed handleProjectRequestSubmit (THIS WAS MISSING)
    const handleProjectRequestSubmit = useCallback(async (stageStatus: string, remarks: string) => {
        setSubmitLoader(true);
        const data = {
            user_id: user_id,
            project_id: project_id,
            dk_stage: stage.dk_stage,
            project_stage_id: stage.project_stage_id,
            action: stageStatus,
            remarks: remarks
        }

        let stageSubmitResp: ApiResponse = await updateProjectsStagesStatus(data);
        if (stageSubmitResp.status === "ok") {
            showSnackbar(stageSubmitResp.message, 'success');
            if (stageStatus == stageCompletedCode) {
                const data = {
                    user_id: user_id,
                    project_id: project_id,
                    dk_stage: stage.dk_stage
                }
                // await getProjectReport(data, stage.dk_stage);
                navigate(`/dashboard`);
            } else {
                navigate(`/dashboard`);
            }
        } else {
            setSubmitLoader(false);
            showSnackbar(stageSubmitResp.message, 'danger');
        }
    }, [user_id, project_id, stage.dk_stage, stage.project_stage_id, navigate, showSnackbar]);

    //  Fixed loadStepper
    const loadStepper = useCallback(() => {
        const CurrentStepComponent = currentStep.step.components;
        const StepComponent = CurrentStepComponent as React.ComponentType<{
            submitLoader: boolean;
            project_id: string;
            disableFormFields: boolean;
            setInitValues: React.Dispatch<React.SetStateAction<IStrategicResourcingStepTypes>>;
            isApprover: boolean;
            stage: IProjectStage;
            handleProjectRequestSubmit: (stageStatus: string, remarks: string) => void,
            setIsAllowNextStep: React.Dispatch<React.SetStateAction<boolean>>
        }>;

        return (
            <Suspense fallback={<div className="p-4 text-center">Loading step...</div>}>
                <StepComponent
                    submitLoader={submitLoader}
                    project_id={project_id}
                    disableFormFields={disableFormFields}
                    setInitValues={setInitValues}
                    isApprover={isApprover}
                    stage={stage}
                    handleProjectRequestSubmit={handleProjectRequestSubmit}
                    setIsAllowNextStep={setIsAllowNextStep}
                />
            </Suspense>
        );
    }, [currentStep.step.components, submitLoader, project_id, disableFormFields, isApprover, stage, handleProjectRequestSubmit]);

    //  Fixed handleFormSubmit to prevent double submission
    const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>, handleSubmit: any, setFieldValue: any) => {
        e.preventDefault();

        const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
        const action = submitter?.value;

        if (action) {
            await setFieldValue("formAction", action);
        }

        // Call handleSubmit directly without calling requestSubmit again
        handleSubmit(e);
    }, []);

    if (!initValues) {
        return <NotFound />;
    }

    return (
        <div
            className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid'
            id='project_stepper'
        >
            <Formik<IStrategicResourcingStepTypes> innerRef={formikRef} validationSchema={currentSchema} enableReinitialize={true} initialValues={initValues} onSubmit={submitStep}>
                {({ handleSubmit, setFieldValue, dirty, values }: FormikProps<IStrategicResourcingStepTypes>) => {
                    return <>
                        {/* begin::Aside*/}
                        <StepIconSidebar
                            steps={stepperData}
                            currentStep={currentStep}
                            onStepClick={(key) => goToStep(key, dirty)}
                        />
                        {/* begin::Aside*/}
                        <div className='d-flex flex-row-fluid justify-content-center bg-body rounded'>
                            <Form className='p-6 w-100 position-relative' noValidate id='kt_create_account_form' onSubmit={(e) => handleFormSubmit(e, handleSubmit, setFieldValue)}>
                                <div className='card card-custom'>
                                    <div className='card-header align-items-center'>
                                        <div className='d-flex align-items-center'>
                                            <DynamicSymbol
                                                size={40}
                                                bgClass={`${currentStep.step.iconColor} ${currentStep.step.iconBackground}`}
                                                icon={currentStep.step.icon ? currentStep.step.icon : <CircleDot size={22} />}
                                            />
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <h3 className='card-title align-items-center mb-0 mt-0'>{currentStep.step.title}</h3>
                                                    {currentStep.step.tooltip && <ThemeTooltip text={currentStep.step.tooltip} />}
                                                </div>
                                                {currentStep.step.description && <div className="text-muted fs-7 mt-0">{currentStep.step.description}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='card-body pt-2 px-6 pb-4'>
                                        {loadStepper()}
                                        {(!disableFormFields || (currentStep.isLast && canSubmit)) &&
                                            <div className='d-flex flex-stack pt-6 pb-0 px-4'>
                                                <div className='mr-2'>{!currentStep.isFirst &&
                                                    <button
                                                        onClick={() => prevStep(dirty)}
                                                        type='button'
                                                        className='btn btn-md btn-light-primary me-3'
                                                    >
                                                        <KTIcon iconName='arrow-left' className='fs-4 me-1' />
                                                        Back
                                                    </button>}
                                                </div>
                                                <div>
                                                    {currentStep.step.showContinue ?
                                                    <> {isAllowNextStep && 
                                                        <button disabled={submitLoader} type='button' className='btn btn-md btn-primary ms-3' onClick={handleContinue}>
                                                            <span className='indicator-label d-flex flex-center'>
                                                                {'Next'}
                                                                <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
                                                            </span>
                                                        </button> }
                                                        </>
                                                        : <>
                                                            {!currentStep.isLast ? <>
                                                                <button disabled={submitLoader} type='submit' value='save' className='btn btn-md btn-active-primary btn-color-primary border border-primary btn-active-color-white save-only'>
                                                                    <span className='indicator-label d-flex flex-center'>
                                                                        {submitLoader && values.formAction === "save" ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Saving...</span></> : "Save"}
                                                                    </span>
                                                                </button>
                                                                {isAllowNextStep &&
                                                                    <button disabled={submitLoader} type='submit' value='save-next' className='btn btn-md btn-primary ms-3'>
                                                                        <span className='indicator-label d-flex flex-center'>
                                                                            {submitLoader && values.formAction === "save-next" ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Saving & opening next...</span></> : "Save & Next"}
                                                                            <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
                                                                        </span>
                                                                    </button>} </> :
                                                                canSubmit && <button disabled={submitLoader} onClick={handleSubmitStage} type='button' className='btn btn-md btn-primary'>
                                                                    <span className='indicator-label d-flex flex-center'>
                                                                        {submitLoader ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Submitting...</span></> : "Submit"}
                                                                    </span>
                                                                </button>
                                                            }
                                                        </>
                                                    }
                                                    <Field type="hidden" name="formAction" />
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </Form>
                            <StepChangeAlertModal
                                popupLoader={submitLoader}
                                show={stepChangeAlertModal}
                                onClose={() => setStepChnageAlertModal(false)}
                                onConfirm={() => confirmStepChange()}
                                onSave={() => handleSaveAndContinue(values)}
                                message={`Your updated data in this step will be lost if you move to another step without saving. Do you want to continue?`}
                            />
                        </div>
                    </>
                }}
            </Formik>
            <SubmitConfirmationModal
                text="After submitting this stage, you will not be able to make any changes. Are you sure you want to submit this stage?"
                loader={submitLoader}
                show={showSubmitConfirmationModal}
                onClose={() => setShowSubmitConfirmationModal(false)}
                onConfirm={handleConfirmSubmitStage}
            />
        </div>
    )
}

export default StrategicResourcing